import { useMemo } from 'react';

import type { Community } from '~/interfaces/community';

export const hexToRgb = (hex: string): string => {
  const stripped = hex.replace('#', '');
  const expanded =
    stripped.length === 3
      ? stripped
          .split('')
          .map((c) => c + c)
          .join('')
      : stripped;
  const r = Number.parseInt(expanded.slice(0, 2), 16);
  const g = Number.parseInt(expanded.slice(2, 4), 16);
  const b = Number.parseInt(expanded.slice(4, 6), 16);
  return `${r} ${g} ${b}`;
};

/** A crude midpoint is enough: the stored text colour is white or black, never a mid-tone. */
export const hasLightTextOnBackground = (textOnBackground: string | null | undefined): boolean => {
  if (!textOnBackground) {
    return false;
  }
  const [red, green, blue] = hexToRgb(textOnBackground).split(' ').map(Number);
  return (red + green + blue) / 3 > 127;
};

// The soft primary is a light shade of the primary for mentions on white cards, whatever the community background,
// so it mixes with white and black.
// The mention border matches the chip, so it only shows on near-white chips: above L 0.97 it darkens to about L 0.90,
// which keeps the chip visible on the white card.
// The border is not in an @supports block, because the website comments inject these tokens in a plain :root.
// Browsers without relative colors compute no box-shadow, so the chip shows as before.
export const chipColorTokens = (textOnPrimary: string): string => {
  const isDarkPrimary = hasLightTextOnBackground(textOnPrimary);
  const colors = isDarkPrimary
    ? `
    --community-primary-soft: color-mix(in oklab, var(--community-primary) 15%, white 85%);
    --community-text-on-primary-soft: color-mix(in oklab, var(--community-primary) 70%, black 30%);`
    : `
    --community-primary-soft: color-mix(in oklab, var(--community-primary) 40%, white 60%);
    --community-text-on-primary-soft: color-mix(in oklab, var(--community-primary) 35%, black 65%);`;
  return `${colors}
    --community-mention-bg: var(--community-primary-soft);
    --community-mention-text: var(--community-text-on-primary-soft);
    --community-mention-border: oklch(from var(--community-mention-bg) calc(l - max(0, l - 0.97) * 3.3) c h);
  `;
};

const DEFAULTS = {
  primary: '#030712',
  textOnPrimary: '#FFFFFF',
  bg: '#FFFFFF',
  textOnBg: '#000000',
} as const;

/**
 * Builds a CSS string that sets community theme colors as custom properties on :root.
 * Rendered via a <style> tag in the community layout for deterministic, flash-free theming
 * that works on SSR and cascades into portaled elements (modals, dropdowns, overlays).
 */
type CommunityThemeColors = Pick<
  Community,
  'primary_color' | 'text_on_primary_color' | 'background_color' | 'text_on_background_color'
>;

export const useCommunityThemeStyle = (community: CommunityThemeColors | null | undefined): string => {
  return useMemo(() => {
    const primary = community?.primary_color ?? DEFAULTS.primary;
    const textOnPrimary = community?.text_on_primary_color ?? DEFAULTS.textOnPrimary;
    const bg = community?.background_color ?? DEFAULTS.bg;
    const textOnBg = community?.text_on_background_color ?? DEFAULTS.textOnBg;

    return `:root {
  --community-primary: ${primary};
  --community-primary-rgb: ${hexToRgb(primary)};
  --community-primary-on-light: var(--community-primary);
  --community-accent: var(--community-primary);
  --community-text-on-primary: ${textOnPrimary};
  --community-text-on-primary-rgb: ${hexToRgb(textOnPrimary)};
  --community-bg: ${bg};
  --community-bg-rgb: ${hexToRgb(bg)};
  --community-text-on-bg: ${textOnBg};
  --community-text-on-bg-rgb: ${hexToRgb(textOnBg)};
  ${chipColorTokens(textOnPrimary)}
}
@supports (color: oklch(from red l c h)) {
  :root {
    --community-primary-on-light: oklch(from var(--community-primary) min(l, 0.5) c h);
    --community-accent: hsl(from var(--community-primary) h s calc(l * 0.85));
  }
}`;
  }, [community]);
};
